import { parseEther } from '@ethersproject/units';
import {
    StakingRewardsDto,
    UserIncentivizedRewardsDto,
    UserSpoolDto,
} from '../types/spool-local';
import snapshot from '@snapshot-labs/snapshot.js';
import { config } from '../config/config';
import { SupportedChains } from '../config/constants';

export const getIncentivizeEarningsTotal = (earnings: UserIncentivizedRewardsDto[]) => {
    const total = earnings.reduce((earningsTotal, { claimableAmount }) => {
        return earningsTotal.add(parseEther(claimableAmount));
    }, parseEther('0'));

    return total;
};

export const getStakingRewardsTotal = (stakingRewards: StakingRewardsDto) => {
    const total = Object.values(stakingRewards).reduce(
        (stakingRewardsTotal, stakingReward) => {
            const stakingRewardTotal = Object.values(stakingReward).reduce(
                (rewardTotal, reward) => {
                    return rewardTotal.add(parseEther(reward.toString()));
                },
                parseEther('0')
            );

            return stakingRewardsTotal.add(stakingRewardTotal);
        },
        parseEther('0')
    );

    return total;
};

export const isUserSpoolKey = (key: any): key is keyof UserSpoolDto => {
    const userSpoolDtoKeys: (keyof UserSpoolDto)[] = [
        'id',
        'pendingDepositAmount',
        'pendingWithdrawalAmount',
        'user',
        'userClaimableAmount',
        'userCurrentBalance',
        'userIncentivizedRewards',
        'userShares',
        'userYieldEarnedToDateAmount',
    ];

    return userSpoolDtoKeys.includes(key);
};

export const getRiskScore = (riskTolerance: number) => {
    const risk = mapRiskToRange(riskTolerance);

    switch (true) {
        case risk > 5:
            return 'Risk Seeking';
        case risk === 5:
            return 'Risk Neutral';
        default:
            return 'Risk Averse';
    }
};

export const mapRiskToRange = (risk: number, reverse: boolean = false) => {
    const oldRange = [-10, 10];
    const newRange = [0, 10];

    const inMin = reverse ? newRange[0] : oldRange[0];
    const inMax = reverse ? newRange[1] : oldRange[1];

    const outMin = reverse ? oldRange[0] : newRange[0];
    const outMax = reverse ? oldRange[1] : newRange[1];

    return ((risk - inMin) / (inMax - inMin)) * (outMax - outMin) + outMin;
};

const STRATEGIES = [
    {
        name: 'contract-call',
        params: {
            symbol: 'voSPOOL',
            address: '0xaF56D16a7fe479F2fcD48FF567fF589CB2d2a0E9',
            decimals: 18,
            methodABI: {
                name: 'getUserGradualVotingPower',
                type: 'function',
                inputs: [
                    {
                        name: 'user',
                        type: 'address',
                        internalType: 'address',
                    },
                ],
                outputs: [
                    {
                        name: '',
                        type: 'uint256',
                        internalType: 'uint256',
                    },
                ],
                stateMutability: 'view',
            },
        },
    },
];

type ScoreResponseType = {
    votes: {
        voter: string;
        choice: Record<string, number>;
    }[];
    proposal: {
        snapshot: number;
        choices: string[];
    };
};

export const getProposalScores = async (proposal: string) => {
    try {
        const res: ScoreResponseType = await snapshot.utils.subgraphRequest(
            `${config.snapshot.SNAPSHOT_HUB_PRODUCTION}/graphql`,

            {
                votes: {
                    __args: {
                        first: 999,
                        where: {
                            proposal,
                        },
                    },
                    voter: true,
                    choice: true,
                },
                proposal: {
                    __args: {
                        id: proposal,
                    },
                    snapshot: true,
                    choices: true,
                },
            }
        );

        const userVotingPower = await snapshot.utils.getScores(
            config.snapshot.SNAPSHOT_SPACE,
            STRATEGIES,
            SupportedChains.MAINNET.toString(),
            res.votes.map(({ voter }) => voter),
            Number(res.proposal.snapshot)
        );

        const totalScores = res.proposal.choices.reduce((total, choice, index) => {
            const totalChoiceVotes = res.votes.reduce((accumulatedChoiceVote, user) => {
                const totalUserChoice = Object.values(user.choice).reduce(
                    (totalVotedAmount, votedAmount) => (totalVotedAmount += votedAmount),
                    0
                );

                return user.choice?.[index + 1]
                    ? accumulatedChoiceVote +
                          (user.choice[index + 1] / totalUserChoice) *
                              userVotingPower[0][user.voter]
                    : accumulatedChoiceVote;
            }, 0);

            return {
                ...total,
                [choice.toLowerCase()]: totalChoiceVotes,
            };
        }, {} as Record<string, number>);

        const totalVotingScore = Object.values(totalScores).reduce(
            (total, score) => (total += score),
            0
        );

        const relatives = Object.entries(totalScores).reduce(
            (totalRelative, [choice, choiceScore]) => ({
                ...totalRelative,
                [choice.toLowerCase()]: choiceScore / totalVotingScore,
            }),
            {} as Record<string, number>
        );

        return {
            absoluteScores: totalScores,
            relativeScores: relatives,
        };
    } catch (error) {
        console.log(error);
        return {
            absoluteScores: {},
            relativeScores: {},
        };
    }
};
